/* eslint-disable vue/one-component-per-file */
import { h, defineComponent, PropType, ComponentOptions } from 'vue';
import {
  ColDef,
  ICellRendererParams,
  RowHeightParams,
  ValueGetterParams,
  GridOptions,
  RowNode
} from 'ag-grid-community';
import { PlainObject } from '../../core/types';
import { fixVNodeClickEvent } from '../../core/CommonInternal';

import { isArray, isFunction, isPlainObject } from 'lodash-es';

export type MergeType<A, B> = {
  [K in keyof (A & B)]: K extends keyof B
    ? B[K]
    : K extends keyof A
    ? A[K]
    : never;
};

// 两个类合并成一个类
function buildMergeClass(a: PlainObject, b: PlainObject) {
  return class MergeClass {
    [index: string]: any; //index signature

    constructor() {
      Object.keys(a).forEach((key) => {
        this[key] = a[key];
      });

      Object.keys(b).forEach((key) => {
        this[key] = b[key];
      });
    }
  };
}

// detail class
export function DetailClass<D extends PlainObject, M extends PlainObject>(
  dctor: new () => D,
  mctor: new () => M
) {
  const d = new dctor();
  const m = new mctor();

  return buildMergeClass(d, m) as new () => MergeType<D, M>;
}

type CellComponent<M extends PlainObject, D extends PlainObject> =
  | ((params: ICellRendererParams<MasterData<M, D>, any>) => JSX.Element)
  | ComponentOptions<{ params: ICellRendererParams<MasterData<M, D>, any> }>;

type MasterData<M extends PlainObject, D extends PlainObject> = M & {
  __details: D[];
};

type DetailData<M extends PlainObject, D extends PlainObject> = MergeType<
  M,
  D
> & {
  __master: M; // master
};

/**
 * master 主row
 * detail 子row
 */
export type MasterDetailDef<M extends PlainObject, D extends PlainObject> = {
  // 是否可打印, 开启打印将关闭虚拟化dom
  printable?: boolean;

  // master定义
  master: {
    // master的主键
    id: keyof M;

    // 与detail不一样高度时, 此处指定(detail高度通过:row-height指定)
    height?: number;

    // master的自定义组件
    component: CellComponent<M, D>;
  };

  // detail定义
  details: {
    // detail的主键
    id: keyof D;

    // 行合并
    // 针对每个列定义，返回number[], 表示合并的行数
    // 未指定列不合并
    rowSpan?: Partial<
      Record<
        keyof M | keyof D,
        (rows: DetailData<M, D>[], master: M) => number[]
      >
    >;

    // warning:
    // 一个master下的detail最大行数(默认20, 如果details长度超过maxRows, 会被trim掉)
    // printable为true情况下, 不需要此参数
    maxRows?: number;

    // 从结果中提取detail数据
    details: (m: M, ctx: { index: number } & PlainObject) => D[];
  };
};

// 第一列
function buildComponent(component: CellComponent<any, any>) {
  if (isFunction(component)) {
    return defineComponent({
      props: {
        params: {
          type: Object as PropType<ICellRendererParams<any, any>>,
          required: true
        }
      },

      setup(props) {
        return () => {
          try {
            return fixVNodeClickEvent((component as any)(props.params));
          } catch (e: any) {
            console.error('CellRenderer Error: ', e);
            return () => [];
          }
        };
      }
    });
  }

  return component;
}

function detailComponentWrap(
  component: ComponentOptions<{ params: ICellRendererParams<any, any> }>
) {
  return defineComponent({
    props: {
      params: {
        type: Object as PropType<ICellRendererParams<any, any>>,
        required: true
      }
    },

    setup(props) {
      return () => {
        const params = props.params;
        const field = params.colDef!.field!;
        // 不显示
        if (params.data[`__skip_${field}`]) {
          return <span />;
        }

        return h(component as any, { params });
      };
    }
  });
}

function fullValueGetter(params: ValueGetterParams<any>) {
  return JSON.stringify(params.data, (key, value) => {
    if (key.startsWith('_')) {
      return undefined;
    }
    return value;
  });
}

export function useMasterDetails<M extends PlainObject, D extends PlainObject>(
  colDefs: ColDef[],
  options: GridOptions<any>,
  masterDetailDef: MasterDetailDef<M, D> | undefined,
  redraw: () => void
) {
  if (!masterDetailDef) {
    return {
      enableMasterDetail: undefined,
      preprocess: (data: M[]) => data
    };
  }

  const totalColumns =
    colDefs.length - colDefs.filter((c) => c.pinned == 'left').length + 1;

  const masterCol: ColDef = {
    field: '__m',
    headerName: '',
    sortable: false,
    maxWidth: 0,
    width: 0,
    suppressMovable: true,
    lockPosition: 'left',

    cellClassRules: {
      'ag-master-cell': (params) => !!params.data['__details']
    },

    // 列合并
    colSpan: (params) => {
      const data = params.data;
      // master
      if (data['__details']) {
        // 占整行
        return totalColumns;
      }

      return 1;
    }
  };

  const firstColIndex = colDefs.findIndex((e) => !e.pinned);
  if (firstColIndex >= 0) {
    colDefs.splice(firstColIndex, 0, masterCol);
  } else {
    colDefs.push(masterCol);
  }

  const masterComponent = buildComponent(masterDetailDef.master.component);
  const emptyComponent = buildComponent(() => <span />);

  for (const colDef of colDefs) {
    // 禁止排序
    colDef.sortable = false;
    // 不允许拖动
    //colDef.suppressMovable = true;
    // 禁止autoHeight
    colDef.autoHeight = false;

    if (colDef.pinned) continue;

    const detailComponent = isPlainObject(colDef.cellRenderer)
      ? detailComponentWrap(colDef.cellRenderer)
      : undefined;

    const detailParams = colDef.cellRendererParams;
    const cellRendererSelector = colDef.cellRendererSelector;

    // 改变ag-grid缓存
    const valueGetter = colDef.valueGetter;
    colDef.valueGetter = (params) => {
      const data = params.data;
      // master
      if (data['__details']) {
        if (colDef == masterCol) {
          return fullValueGetter(params);
        }
        return '';
      }

      // 填充行
      if (data['__skip']) {
        return '';
      }

      // detail
      const field = params.colDef!.field!;
      // 不显示
      if (data[`__skip_${field}`]) {
        return '';
      }

      if (isFunction(valueGetter)) {
        return valueGetter(params);
      }

      // if (detailComponent) {
      //   return fullValueGetter(params);
      // }

      // TODO cellRendererSelector

      return data[field];
    };

    // 渲染组件
    colDef.cellRendererSelector = (params) => {
      const data = params.data;
      // master
      if (data['__details']) {
        if (colDef == masterCol) {
          return {
            component: masterComponent
          };
        }

        return {
          component: emptyComponent
        };
      }

      // 填充行
      if (data['__skip']) {
        return {
          component: emptyComponent
        };
      }

      // detail
      const field = params.colDef!.field!;
      // 不显示
      if (data[`__skip_${field}`]) {
        return {
          component: emptyComponent
        };
      }

      if (detailComponent) {
        return {
          component: detailComponent,
          params: detailParams
        };
      }

      if (cellRendererSelector) {
        const result = cellRendererSelector(params);
        if (result) {
          const { component, params } = result;

          return {
            component: detailComponentWrap(component),
            params
          };
        }
      }

      return undefined;
    };
  }

  // 行合并
  if (masterDetailDef.details.rowSpan) {
    for (const [field, span] of Object.entries(
      masterDetailDef.details.rowSpan
    )) {
      if (!span) continue;

      const index = colDefs.findIndex((c) => c.field == field);
      if (index < 0) {
        continue;
      }

      const colDef = colDefs[index];

      colDef.cellClassRules = {
        ...(colDef.cellClassRules || {}),
        'show-cell': (params) => !!params.data['__master']
      };

      colDef.rowSpan = (params) => {
        const data = params.data;
        // master
        if (data['__details']) {
          return 1;
        }

        const master = data['__master'] as M;
        const details = master['__details'] as DetailData<M, D>[];

        let index = details.findIndex((e) => e == data);
        const spans = span(details, master);

        for (const s of spans) {
          if (!s) continue;

          if (index == 0) {
            return s;
          }

          index -= s;
          if (index < 0) {
            break;
          }
        }

        // 标记cell为不需要渲染
        data[`__skip_${field}`] = true;
        return 1;
      };
    }
  }

  const mid = masterDetailDef.master.id;
  const did = masterDetailDef.details.id;

  const getRowId = options.getRowId || ((params: any) => '' + params.data[did]);
  options.getRowId = (params) => {
    const data = params.data!;
    if (data['__details']) {
      return '?' + (data as any)[mid];
    }

    return getRowId(params);
  };

  // 行高
  const getRowHeight = options.getRowHeight;

  const masterHeight = masterDetailDef.master.height;

  options.getRowHeight = masterHeight
    ? (params: RowHeightParams<any>) => {
        const data = params.data;
        // master
        if (data['__details']) {
          return masterHeight;
        }

        return getRowHeight?.(params);
      }
    : getRowHeight;

  options.suppressRowTransform = true;

  let maxRows: number;
  // 打印不能开启虚拟化
  if (masterDetailDef.printable) {
    options.suppressColumnVirtualisation = true;
    options.suppressRowVirtualisation = true;

    maxRows = Number.MAX_SAFE_INTEGER;
  } else {
    // 设置最大虚拟化缓存
    maxRows = masterDetailDef.details.maxRows || 20;
    options.rowBuffer = maxRows;
  }

  // 行点击处理
  const onRowSelected = options.onRowSelected;
  options.onRowSelected = (event) => {
    if (event.event?.type == 'easy-select') {
      onRowSelected?.(event);
      return;
    }

    const api = event.api;
    const data = event.data;

    const selected = event.node.isSelected();

    // master
    if (data['__details']) {
      // 选中所有的detail
      const details = data['__details'] as any[];
      for (const detail of details) {
        // 空白行
        if (detail['__skip']) {
          continue;
        }

        const n = api.getRowNode(detail[did]);
        selectThisNode(n, selected);
      }
    } else {
      // 点击detail
      const master = data['__master'];
      const details = master['__details'] as any[];
      const masterNode = api.getRowNode('?' + master[mid]);

      if (selected) {
        // 全选中
        const allSelected = details.every((e) => {
          // 空白行
          if (e['__skip']) {
            return true;
          }

          const n = api.getRowNode(e[did]);
          return n?.isSelected();
        });
        if (allSelected) {
          selectThisNode(masterNode, true);
        } else {
          selectThisNode(masterNode, undefined);
        }
      } else {
        // 全不选中
        const allUnselected = details.every((e) => {
          // 空白行
          if (e['__skip']) {
            return true;
          }

          const n = api.getRowNode(e[did]);

          return !n?.isSelected();
        });
        if (allUnselected) {
          selectThisNode(masterNode, false);
        } else {
          selectThisNode(masterNode, undefined);
        }
      }
    }

    onRowSelected?.(event);
  };

  // const onRowDataUpdated = options.onRowDataUpdated;
  // options.onRowDataUpdated = (event) => {
  //   console.info('onRowDataUpdated', event);

  //   onRowDataUpdated?.(event);
  // };

  function selectThisNode(node?: RowNode<any>, selected?: boolean) {
    if (!node) {
      return;
    }

    const isSelected = node.isSelected();
    if (isSelected === selected) return;

    if (selected === undefined) {
      node.selectThisNode(selected, new CustomEvent('easy-select'));
    } else {
      node.setSelectedParams({
        newValue: selected,
        event: new CustomEvent('easy-select')
      });
    }
  }

  const isRowSelectable = options.isRowSelectable;
  options.isRowSelectable = (params) => {
    const data = params.data;

    // 空白行
    if (data['__skip']) {
      return false;
    }

    if (isRowSelectable) {
      return isRowSelectable(params);
    }

    return true;
  };

  // 增加上css
  /*options.rowClassRules = {
    ...(options.rowClassRules || {}),
    'easy-ag-master': (params) => !!params.data['__details'],
    'easy-ag-detail': (params) => !!params.data['__master']
  };*/

  let realMaxRows = 0;

  // 构建数据, 将master, details拉平
  return {
    enableMasterDetail: true,
    preprocess: (data: M[]) => {
      const list: DetailData<M, D>[] = [];

      const ctx: { index: number } & PlainObject = { index: 0 };

      realMaxRows = 0;

      for (const m of data) {
        list.push(m as any);

        const details = masterDetailDef.details.details(m, ctx);
        realMaxRows = details.length;
        if (realMaxRows > maxRows) {
          console.warn(
            `masterDetails rows:${details.length} exceed ${maxRows}`
          );
        }

        details.forEach((detail) => {
          for (const [k, v] of Object.entries(m)) {
            // 避免循环引用
            if (detail[k] === undefined && !isPlainObject(v) && !isArray(v)) {
              (detail as any)[k] = v;
            }
          }
          (detail as any)['__master'] = m;

          list.push(detail as any);
        });

        (m as any)['__details'] = details;
        (m as any)['__uncount'] = true; // 不计行数
      }

      if (!masterDetailDef.printable) {
        // 升级maxRows
        if (realMaxRows > (options.rowBuffer || 0)) {
          options.rowBuffer = realMaxRows;
          redraw();
        } else if ((options.rowBuffer || 0) > maxRows) {
          const rowBuffer = Math.max(maxRows, realMaxRows);
          if (rowBuffer != options.rowBuffer) {
            // 降级
            options.rowBuffer = Math.max(maxRows, realMaxRows);
            redraw();
          }
        }
      }

      return list;
    }
  };
}
